import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { paths } from '../api/schema.js';
import { formatDate, formatNumber } from '../i18n.js';
import { flagOf, regionName } from './geo.js';
import { systemLabel } from './site.js';
import { SeverityBadge } from './ui.js';

type Overview =
  paths['/sites/{siteId}/overview']['get']['responses'][200]['content']['application/json'];

/** One fact: a label, its value and maybe a link below; `tone` marks a value that needs attention. */
interface Fact {
  key: string;
  label: string;
  value: ReactNode;
  mono?: boolean;
  tone?: 'warn' | 'bad' | 'good' | undefined;
  action?: ReactNode;
}

/** A response this slow counts as slow (the default degraded threshold of the uptime check). */
const SLOW_MS = 3000;

/**
 * "Die Website auf einen Blick" (T-94, site-dashboard.html .kv): where the site runs, what it is
 * built with, certificate, domain, mail and the tools on the home page. Facts not known yet are
 * left out; what only the confirmed owner may see says so (T-101).
 */
export function SiteFacts({
  overview: o,
  timeZone,
}: {
  overview: Overview;
  timeZone?: string | undefined;
}) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const lang = i18n.language;
  const p = o.profile;
  const h = p.hosting;
  const ssl = o.security.ssl;
  const domain = o.security.domain;
  const date = (iso: string) => formatDate(iso, lang, timeZone);
  const settings = `/sites/${o.site.id}/settings`;

  const facts: Fact[] = [];
  // Whether there are weaknesses, always; which ones only for the confirmed owner (T-101).
  const weak = o.weaknesses;
  facts.push({
    key: 'weaknesses',
    label: t('facts.weaknesses'),
    value: !o.health ? (
      t('facts.weaknessesPending')
    ) : weak.count === 0 || !weak.worst ? (
      t('facts.weaknessesNone')
    ) : (
      <>
        {t('facts.weaknessesFound', { n: weak.count })} · <SeverityBadge severity={weak.worst} />
      </>
    ),
    tone: o.health && weak.count === 0 ? 'good' : undefined,
    action:
      o.health && weak.count > 0 ? (
        weak.detailsHidden ? (
          <Link className="lock" to={settings}>
            {t('facts.weaknessesHidden')}
          </Link>
        ) : (
          <Link className="lock" to={`/sites/${o.site.id}/findings`}>
            {t('facts.weaknessesShow')}
          </Link>
        )
      ) : null,
  });
  if (h?.country)
    facts.push({
      key: 'location',
      label: t('facts.location'),
      value: (
        <>
          <span aria-hidden="true">{flagOf(h.country)} </span>
          {regionName(h.country, lang)} ·{' '}
          {h.cdn ? t('facts.viaCdn', { cdn: h.cdn }) : t('facts.noCdn')}
        </>
      ),
    });
  if (h?.org)
    facts.push({
      key: 'hoster',
      label: t('facts.hoster'),
      value: h.asn ? `${h.org} (AS${h.asn})` : h.org,
    });
  if (h) facts.push({ key: 'ip', label: t('facts.ip'), value: h.ip, mono: true });
  const cms = o.site.cmsDetected;
  facts.push({
    key: 'system',
    label: t('facts.system'),
    value: cms ? (
      <>
        {systemLabel(cms, p.wordpressVersion)}
        {/* The version is the owner's to see (T-101): WordPress is the only one we read. */}
        {!o.site.verified && /^wordpress/i.test(cms) ? ` ${t('facts.versionAfterVerify')}` : null}
      </>
    ) : (
      t('facts.unknownSystem')
    ),
  });
  if (ssl) {
    const broken = ssl.problems.length > 0 || (ssl.daysLeft !== null && ssl.daysLeft < 0);
    facts.push({
      key: 'ssl',
      label: t('facts.ssl'),
      mono: !broken,
      value: broken
        ? t('facts.sslProblem')
        : ssl.validTo
          ? t('facts.until', { date: date(ssl.validTo), count: Math.max(0, ssl.daysLeft ?? 0) })
          : '–',
      tone: broken ? 'bad' : ssl.daysLeft !== null && ssl.daysLeft < 14 ? 'warn' : undefined,
    });
  }
  if (domain)
    facts.push({
      key: 'domain',
      label: t('facts.domain'),
      value: (
        <>
          {domain.expiresAt
            ? t('facts.domainUntil', {
                date: date(domain.expiresAt),
                count: Math.max(0, domain.daysLeft ?? 0),
              })
            : domain.noExpiryPublished
              ? t('facts.noExpiry')
              : '–'}
          {p.registrar ? ` · ${t('facts.registrar', { name: p.registrar })}` : null}
        </>
      ),
      tone: domain.daysLeft !== null && domain.daysLeft < 30 ? 'warn' : undefined,
    });
  if (p.email)
    facts.push({
      key: 'email',
      label: t('facts.email'),
      value: (
        <>
          {t(p.email.mx ? 'facts.mailOn' : 'facts.mailOff')}
          {p.email.ownerOnly
            ? ` · ${t('facts.mailAfterVerify')}`
            : ` · SPF: ${t(p.email.spf ? 'facts.yes' : 'facts.no')} · DMARC: ${
                p.email.dmarc ? t(`facts.dmarc.${p.email.dmarc}`) : t('facts.no')
              }`}
        </>
      ),
      tone:
        !p.email.ownerOnly && p.email.mx && (!p.email.spf || !p.email.dmarc) ? 'warn' : undefined,
    });
  if (o.availability.responseMs !== null)
    facts.push({
      key: 'response',
      label: t('facts.response'),
      mono: true,
      value: `${formatNumber(o.availability.responseMs, lang, 0)} ms`,
      tone: o.availability.responseMs >= SLOW_MS ? 'warn' : undefined,
    });
  if (o.availability.uptime30d !== null)
    facts.push({
      key: 'uptime',
      label: t('facts.uptime30d'),
      mono: true,
      value: `${formatNumber(o.availability.uptime30d, lang, 2)} %`,
      tone: o.availability.uptime30d < 99 ? 'warn' : undefined,
    });
  if (p.trackers || p.cookieBanners) {
    const trackers = p.trackers ?? [];
    const banners = p.cookieBanners ?? [];
    facts.push({
      key: 'trackers',
      label: t('facts.trackers'),
      value: [
        trackers.length > 0 ? trackers.join(', ') : t('facts.noneFound'),
        banners.length > 0
          ? t('facts.withBanner', { names: banners.join(', ') })
          : trackers.length > 0
            ? t('facts.noBanner')
            : null,
      ]
        .filter(Boolean)
        .join(' · '),
      tone: trackers.length > 0 && banners.length === 0 ? 'warn' : undefined,
    });
  }
  facts.push({
    key: 'owner',
    label: t('facts.owner'),
    value: o.site.verified ? t('facts.verified') : t('facts.notVerified'),
    tone: o.site.verified ? 'good' : 'warn',
    action: o.site.verified ? null : (
      <Link className="lock" to={settings}>
        {t('facts.verifyCta')}
      </Link>
    ),
  });

  return (
    <section className="card" aria-labelledby={titleId}>
      <h2 id={titleId}>{t('facts.title')}</h2>
      <dl className="kv">
        {facts.map((f) => (
          <div key={f.key}>
            <dt>{f.label}</dt>
            <dd className={[f.mono ? 'mono' : '', f.tone ?? ''].join(' ').trim() || undefined}>
              {f.value}
            </dd>
            {f.action ? <dd>{f.action}</dd> : null}
          </div>
        ))}
      </dl>
    </section>
  );
}
